import React, { useCallback, useMemo, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alertRulesActions, alertRulesTypes } from 'app-components/flux/alert-rules';
import { makeStyles } from 'tss-react/mui';

import EditIcon from '@mui/icons-material/Edit';
import { IconButton, TextField } from '@mui/material';
import type { AlertRuleProps } from 'app-components/flux/alert-rules/alertRulesTypes';
import QButton from 'components/QButton';
import type { RecordOf } from 'immutable';

const useStyles = makeStyles()((theme) => ({
  formatRow: {
    display: 'flex',
    flexDirection: 'column',
  },
  subtitleItem: {
    display: 'flex',
    alignItems: 'center',
  },
  editNotificationButton: {
    height: 18,
    width: 18,
    color: theme.palette.primary.main,
  },
  marginLeft: {
    marginLeft: theme.spacing(1),
  },
  description: {
    alignItems: 'center',
    color: theme.palette.text.secondary,
    lineHeight: 2,
    '@media (max-width: 1094px)': {
      textWrap: 'nowrap',
    },
  },
  inputField: {
    maxWidth: 90,
    borderColor: theme.palette.divider,
    '&:focus, &:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

type InputValuesType = { default: string; format: 'currency' | 'percentage' };

function NotificationSubtitle({ alertRule }: { alertRule: RecordOf<AlertRuleProps> }) {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const [edit, setEdit] = useState(false);
  const [variables, setVariables] = useState({});

  const description = useMemo(() => {
    let result = alertRule.description ?? '';
    if (alertRule.inputVariables) {
      result = Object.entries(alertRule.inputVariables).reduce(
        (descriptionTmp, [key, value]: [string, InputValuesType]) => {
          const currentValue = alertRule.inputValues?.[key] ?? value.default;
          let valueFormatted;
          switch (value.format) {
            case 'percentage':
              valueFormatted = `${currentValue}%`;
              break;
            case 'currency':
              valueFormatted = `$${currentValue}`;
              break;
            default:
              valueFormatted = currentValue;
          }
          return descriptionTmp.replace(`%${key}%`, valueFormatted);
        },
        result,
      );
    }
    return result;
  }, [alertRule]);

  const editDescription = useMemo(() => {
    let components: (string | JSX.Element)[] = [alertRule.description];
    if (alertRule.inputVariables) {
      components = Object.entries(alertRule.inputVariables).reduce(
        (templates, [key, value]: [string, InputValuesType]) =>
          templates
            .map((template) =>
              typeof template !== 'string'
                ? template
                : template.split(`%${key}%`).reduce(
                    (items, item, index) => [
                      ...items,
                      ...(index
                        ? [
                            ...(alertRule.inputVariables?.[key]?.format === 'currency' ? ['$'] : []),
                            <TextField
                              key={key}
                              id={key}
                              type='number'
                              size='small'
                              variant='outlined'
                              aria-label='parameter'
                              autoFocus
                              inputProps={{
                                style: { fontSize: 14, padding: `5px 8px`, textOverflow: 'ellipsis' },
                              }}
                              InputProps={{ style: { borderRadius: 4 } }}
                              className={classes.inputField}
                              placeholder={alertRule.inputValues?.[key] ?? value.default}
                              defaultValue={alertRule.inputValues?.[key] ?? value.default}
                              onChange={(newValue) => {
                                setVariables({ ...variables, [key]: newValue.target.value });
                              }}
                            />,
                            ...(alertRule.inputVariables?.[key]?.format === 'percentage' ? ['%'] : []),
                          ]
                        : []),
                      item,
                    ],
                    [],
                  ),
            )
            .flat(),
        components,
      );
    }
    return components;
  }, [alertRule.description, alertRule.inputValues, alertRule.inputVariables, classes.inputField, variables]);

  // Handles opening the edit drawer
  const handleDrawerOpen = useCallback(() => {
    setEdit(true);
  }, []);

  // Handles saving the new threshold
  const handleSave = useCallback(() => {
    dispatch(
      alertRulesActions.updateAlertRule(
        alertRulesTypes.mkAlertRuleUpdate({
          id: alertRule.id,
          inputValues: {
            ...variables,
          },
        }),
        { undo: { userMessage: 'Notification settings updated.' } },
      ),
    );
    setEdit(false);
  }, [alertRule, dispatch, variables]);

  const isEditable = Boolean(alertRule.inputVariables);

  return (
    <div className={classes.formatRow}>
      <div className={classes.subtitleItem}>
        <Typography
          variant='paragraphRegular'
          className={classes.description}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handleSave();
            } else if (event.key === 'Escape') {
              setEdit(false);
            }
            event.stopPropagation();
          }}
        >
          {edit ? editDescription : description}
        </Typography>
        {isEditable && !edit && (
          <IconButton
            id={`edit-${alertRule.title}-notification`}
            onClick={handleDrawerOpen}
            className={classes.marginLeft}
          >
            <EditIcon className={classes.editNotificationButton} />
          </IconButton>
        )}
      </div>
      {isEditable && edit && (
        <Box pt={1} display='flex'>
          <QButton id='continue-btn' size='small' variant='contained' onClick={handleSave}>
            Save
          </QButton>
          <QButton
            id='back-btn'
            size='small'
            variant='outlined'
            onClick={() => setEdit(false)}
            className={classes.marginLeft}
          >
            Cancel
          </QButton>
        </Box>
      )}
    </div>
  );
}

export default NotificationSubtitle;
